import { takeEvery, put, call, select } from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import { Action } from 'typescript-fsa'
import {
	GetMediaItemsShowResponse,
	ItemCompact,
	LanguageCode,
	Manifest,
	ManifestKeypadNumbers,
	GetCollectionItemsIndexResponseData,
	GetCollectionsShowResponse,
	GetScreensShowResponse,
	MediaItem,
} from 'mytours-read-api-typescript'

import {
	loadAppMetaData,
	loadedMetaDataComplete,
	changeAppInfo,
	ChangeAppInfoAction,
	unpackInitialAssets,
	appLanguageSelected,
	updateProcessingManifestState,
	ProcessingManifestItem,
	updateProcessingManifestStateItem,
	loadItemOnKeypadEntry,
	LoadItemOnKeypadEntryParams,
} from './actions'
import * as apiActions from '../api/actions'
import { store } from '../root/index'
import { AppMetaData, CollectionItem, Screen as ScreenItem, Collection } from '../api/types'
import { LoadAppMetaDataFromNetworkResult } from '../api/actions'
import { LoadItemOnKeypadEntry } from '../settings/actions'
import { setApiConfig } from '../api/index'
import { checkForUpdates } from '../home/actions'
import { Language } from './types'
import { getConfiguration } from '../configuration/index'
import { FileType } from '../screen/types'
import {
	downloadMediaItemFromAPI,
	downloadScreenFromAPI,
	downloadCollectionDetailsFromAPI,
	downloadCollectionItemsFromAPI,
} from '../api/sagas'
import { convertManifestItem, manifestStateSelector, getCurrentLanguageCode } from './functions'
// import { navigateToCollection, navigateToScreen } from '../navigation/actions'
// import { loadScreen, loadedScreenFromDisk, LoadedScreenResultAction } from '../screen/actions'
import { loadCollectionDetails } from '../collectionlist/actions'
import { loadedKeypadItemsComplete, loadedKeypadItemsNotFound } from '../scanner/actions'
import { KeypadItem, ScannerItemType } from '../scanner/types'

/**
 * Triggers the loading of app meta data.
 */
function* handleLoadAppMetaData(): SagaIterator {
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId
	const platform = getConfiguration().getPlatform()
	const deviceUUID = getConfiguration().getDeviceUUID()

	// check if exists on Disk
	const metaData: AppMetaData | undefined = yield call(getConfiguration().loadAppMetaDataFromDisk, projectId)
	if (metaData) {
		// update UI theme
		yield call(getConfiguration().changeTheme, metaData)

		// store onto redux state
		yield put(loadedMetaDataComplete(metaData))
	} else {
		// else pull down json from network
		// send the async action to start the network request
		yield put(apiActions.fetchAppMetaData.started({ projectId, devicePlatform: platform, deviceUdid: deviceUUID }))
	}
}

/**
 * After a successful request to fetch meta data we want to store the result to disk so that we do not need to hit the network again (unless the version changes).
 * @param action contains the meta data retrieved from network response, and the app id + version
 */
function* handleLoadedMetaDataFromNetwork(action: LoadAppMetaDataFromNetworkResult): SagaIterator {
	const projectId = action.payload.result.project_id
	const data: AppMetaData = action.payload.result

	// update UI theme
	yield call(getConfiguration().changeTheme, data)

	// we want to store onto redux state after the changeTheme function because we need the new styles before the ReadyGate re-renders the app
	// previously the meta data was set on the fetchAppMetaData.done in the reducer
	yield put(loadedMetaDataComplete(data))

	// we don't need to delete old versions as we only store the latest one (it gets overwritten)
	try {
		yield call(getConfiguration().saveAppMetaDataToDisk, String(projectId), data)
	} catch (error) {
		// Error saving data
	}
}

function* handleUnpackInitialAssets(_action: Action<undefined>): SagaIterator {
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId

	// the function will return early if assets have already been unpacked
	yield call(getConfiguration().handleAppUpgrade, projectId)

	// now lets check the meta data is available on redux state
	if (!state.settings.appMetaData) {
		// not available so load from disk
		yield put(loadAppMetaData())
	} else {
		// update UI theme
		yield call(getConfiguration().changeTheme, state.settings.appMetaData)
	}
}

function* handleCheckForUpdates(): SagaIterator {
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId
	const platform = getConfiguration().getPlatform()
	const deviceUdid: string = getConfiguration().getDeviceUUID()
	yield put(apiActions.fetchAppMetaData.started({ projectId, devicePlatform: platform, deviceUdid: deviceUdid }))
}

function* handleLanguageSelected(action: Action<Language>): SagaIterator {
	// when user changes app languages, update the strings library so uses new language
	const languageCode: string = action.payload.code
	yield call(getConfiguration().changeLocalizationLanguage, languageCode)
}

const TAG: string = 'SAMLOG'
const log: boolean = false

function* handleDownloadedManifest(action: apiActions.LoadManifestFromNetworkResult): SagaIterator {
	// if there's an key number, this means the request was done for loading a screen via the app-wide keypad entry and we should avoid downloading the manifest items
	if (action.payload.params.keypadNumber !== undefined) return

	if (log) console.log(TAG, 'handleDownloadedManifest', action.payload.result)
	const manifest = action.payload.result

	// check if user cancelled the syncing process during downloading of manifest file
	const manifestState = yield select(manifestStateSelector)
	if (manifestState === undefined) return

	const state = store.getState()
	const projectId = state.settings.appInfo.projectId

	const supportedLanguages =
		state.settings.appMetaData !== undefined ? state.settings.appMetaData.supported_languages : undefined
	if (supportedLanguages === undefined) {
		throw Error('Must supported languages when downloading manifest')
	}
	const languages: LanguageCode[] = supportedLanguages // .filter((value, index) => index < 3)

	// store processing manfest info on redux (to display progress to user in UI)
	let items: ProcessingManifestItem[] = []
	// loop through all languages
	for (const language of languages) {
		if (manifest.screens) {
			items = items.concat(
				manifest.screens.map<ProcessingManifestItem>(screen => {
					return convertManifestItem(screen, language, 'screen', 'processing')
				}),
			)
		}
		if (manifest.collections) {
			// collection details
			items = items.concat(
				manifest.collections.map<ProcessingManifestItem>(collection => {
					return convertManifestItem(collection, language, 'collection', 'processing')
				}),
			)
			// also collection items
			items = items.concat(
				manifest.collections.map<ProcessingManifestItem>(collection => {
					return convertManifestItem(collection, language, 'collectionItem', 'processing')
				}),
			)
		}
		if (manifest.media_items) {
			items = items.concat(
				manifest.media_items.map<ProcessingManifestItem>(media => {
					return convertManifestItem(media, language, 'media', 'processing')
				}),
			)
		}
	}
	yield put(updateProcessingManifestState({ event: 'processing', items, allLanguages: languages }))

	// process content for one language at a time
	for (const language of languages) {
		// check if user cancelled the syncing process
		if ((yield select(manifestStateSelector)) === undefined) return

		// parse screens
		if (manifest.screens) {
			if (log) console.log(TAG, 'handleDownloadedManifest', 'start parsing screens')
			const result = yield call(processScreens, manifest.screens, language, projectId)
			if (log) console.log(TAG, 'handleDownloadedManifest', 'finished parsing screens', result)
		}

		// check if user cancelled
		if ((yield select(manifestStateSelector)) === undefined) return

		// parse collections
		if (manifest.collections) {
			if (log) console.log(TAG, 'handleDownloadedManifest', 'start parsing collection details')
			const result = yield call(processCollectionDetails, manifest.collections, language, projectId)
			if (log) console.log(TAG, 'handleDownloadedManifest', 'finished parsing collection details', result)

			if (log) console.log(TAG, 'handleDownloadedManifest', 'start parsing collection details')
			const resultItems = yield call(processCollectionItems, manifest.collections, language, projectId)
			if (log) console.log(TAG, 'handleDownloadedManifest', 'finished parsing collection details', resultItems)
		}

		// check if user cancelled
		if ((yield select(manifestStateSelector)) === undefined) return

		// parse media
		if (manifest.media_items) {
			// create promises to handle each media item, then combine into single promise
			if (log) console.log(TAG, 'handleDownloadedManifest', 'promises ALL start')
			const result = yield call(processMediaItems, manifest.media_items, language, projectId)
			if (log) console.log(TAG, 'handleDownloadedManifest', 'promises ALL finish', language, result)
		}

		// check if user cancelled
		if ((yield select(manifestStateSelector)) === undefined) return

		// completed parsing language
		if (log) console.log(TAG, 'handleDownloadedManifest', 'completed language', language)
		yield put(updateProcessingManifestState({ event: 'languageComplete', completedLanguage: language }))

		// if its the last language, dispatch FINISHED action
		const isLastLanguage: boolean = language === languages[languages.length - 1]
		if (isLastLanguage) {
			yield put(updateProcessingManifestState({ event: 'finished' }))
		}
	}
}

function processScreens(items: ItemCompact[], language: LanguageCode, projectId: string): Promise<boolean[]> {
	const devicePlatform = getConfiguration().getPlatform()
	const deviceUdid: string = getConfiguration().getDeviceUUID()

	const promises: Promise<boolean>[] = items.map(item => {
		const id: number = item.id!
		const version: number = item.version!
		// check if screen already exists on disk
		return getConfiguration()
			.loadScreenFromDisk(projectId, id, version, language)
			.then(existingScreen => {
				if (existingScreen) {
					if (log) console.log(TAG, 'screen already exists for', id)
					const params = convertManifestItem(item, language, 'screen', 'success_existed')
					store.dispatch(updateProcessingManifestStateItem(params))
					return true
				} else {
					// download screen from network
					const params: apiActions.ScreenRequestParams = {
						devicePlatform,
						deviceUdid,
						screenId: id,
						screenVersion: version,
						language: language,
						isPreCaching: true,
					}
					return downloadScreenFromAPI(params).then(result =>
						// delete existing version from disk
						getConfiguration()
							.removeScreenFromDisk(projectId, id, language)
							.then(() =>
								// save new version to disk
								getConfiguration()
									.saveScreenToDisk(projectId, id, version, language, result)
									.then(() => {
										// finished!
										if (log) console.log(TAG, 'completed downloading screen for', id)
										const params = convertManifestItem(item, language, 'screen', 'success_downloaded')
										store.dispatch(updateProcessingManifestStateItem(params))
										return true
									}),
							),
					)
				}
			})
			.catch(error => {
				if (log) console.log(TAG, 'Error downloading screen for', id, error)
				const params = convertManifestItem(item, language, 'screen', 'failed')
				store.dispatch(updateProcessingManifestStateItem(params))
				// TODO sentry log
				return false
			})
	})
	return Promise.all(promises)
}

function processCollectionDetails(items: ItemCompact[], language: LanguageCode, projectId: string): Promise<boolean[]> {
	const devicePlatform = getConfiguration().getPlatform()
	const deviceUdid: string = getConfiguration().getDeviceUUID()

	const promises: Promise<boolean>[] = items.map(item => {
		const id: number = item.id!
		const version: number = item.version!
		// check if collection already exists on disk
		return getConfiguration()
			.loadCollectionDetailsFromDisk(projectId, id, version, language)
			.then(existing => {
				if (existing) {
					if (log) console.log(TAG, 'collection details already exists for', id)
					const params = convertManifestItem(item, language, 'collection', 'success_existed')
					store.dispatch(updateProcessingManifestStateItem(params))
					return true
				} else {
					// download collection from network
					const params: apiActions.CollectionRequestParams = {
						devicePlatform,
						deviceUdid,
						collectionId: id,
						collectionVersion: version,
						language: language,
						isPreCaching: true,
					}
					return downloadCollectionDetailsFromAPI(params).then(result =>
						// delete existing version from disk
						getConfiguration()
							.removeCollectionDetailsFromDisk(projectId, id, language)
							.then(() =>
								// save new version to disk
								getConfiguration()
									.saveCollectionDetailsToDisk(projectId, id, version, language, result)
									.then(() => {
										// finished!
										if (log) console.log(TAG, 'completed downloading collection details for', id)
										const params = convertManifestItem(item, language, 'collection', 'success_downloaded')
										store.dispatch(updateProcessingManifestStateItem(params))
										return true
									}),
							),
					)
				}
			})
			.catch(error => {
				if (log) console.log(TAG, 'Error downloading collection details for', id, error)
				const params = convertManifestItem(item, language, 'collection', 'failed')
				store.dispatch(updateProcessingManifestStateItem(params))
				// TODO sentry log
				return false
			})
	})
	return Promise.all(promises)
}

function processCollectionItems(items: ItemCompact[], language: LanguageCode, projectId: string): Promise<boolean[]> {
	const devicePlatform = getConfiguration().getPlatform()
	const deviceUdid: string = getConfiguration().getDeviceUUID()

	const promises: Promise<boolean>[] = items.map(item => {
		const id: number = item.id!
		const version: number = item.version!
		// check if collection items already exists on disk
		return getConfiguration()
			.loadCollectionItemsFromDisk(projectId, id, version, language)
			.then(existing => {
				if (existing) {
					if (log) console.log(TAG, 'collection items already exists for', id)
					const params = convertManifestItem(item, language, 'collectionItem', 'success_existed')
					store.dispatch(updateProcessingManifestStateItem(params))
					return true
				} else {
					// download collection items from network
					const params: apiActions.CollectionItemsRequestParams = {
						devicePlatform,
						deviceUdid,
						collectionId: id,
						collectionVersion: version,
						collectionSubtype: 'list', // not used anymore
						language: language,
						isPreCaching: true,
					}
					return downloadCollectionItemsFromAPI(params).then(result =>
						// delete existing version from disk
						getConfiguration()
							.removeCollectionItemsFromDisk(projectId, id, language)
							.then(() =>
								// save new version to disk
								getConfiguration()
									.saveCollectionItemsToDisk(projectId, id, version, language, result.data)
									.then(() => {
										// finished!
										if (log) console.log(TAG, 'completed downloading collection items for', id)
										const params = convertManifestItem(item, language, 'collectionItem', 'success_downloaded')
										store.dispatch(updateProcessingManifestStateItem(params))
										return true
									}),
							),
					)
				}
			})
			.catch(error => {
				if (log) console.log(TAG, 'Error downloading collection items for', id, error)
				const params = convertManifestItem(item, language, 'collectionItem', 'failed')
				store.dispatch(updateProcessingManifestStateItem(params))
				// TODO sentry log
				return false
			})
	})
	return Promise.all(promises)
}

/**
 * Needed to create this method to combine array of promises into single promise, and then be able to yield the call to the function that returns the single promise
 * @param items media items to process
 * @param language the current language
 */
function processMediaItems(items: ItemCompact[], language: LanguageCode, projectId: string): Promise<boolean[]> {
	const devicePlatform = getConfiguration().getPlatform()
	const deviceUdid: string = getConfiguration().getDeviceUUID()

	const promises: Promise<boolean>[] = items.map(item => {
		const id: number = item.id!
		const version: number = item.version!

		if (log) console.log(TAG, 'handleDownloadedManifest', 'downloadMediaItemFromAPI', id)
		return getConfiguration()
			.loadMediaItemFromDisk(projectId, id, version, language)
			.then((value: MediaItem | undefined) => {
				// if item already exists on disk, return it
				if (value) {
					if (log) console.log(TAG, 'handleDownloadedManifest', 'loadMediaItemFromDisk EXISTS', id, value)
					return value
				} else {
					if (log) console.log(TAG, 'handleDownloadedManifest', 'loadMediaItemFromDisk DOES NOT EXIST', id)
					// else need to download from network
					const params: apiActions.MediaItemRequestParams = {
						devicePlatform,
						deviceUdid,
						itemId: id,
						itemVersion: version,
						language,
						isPreCaching: true,
					}
					return downloadMediaItemFromAPI(params).then((result: MediaItem) => {
						if (log) console.log(TAG, 'handleDownloadedManifest', 'downloadMediaItemFromAPI Finished', id)
						// TODO remove any old versions first
						// save the media item json to disk
						return getConfiguration()
							.saveMediaItemToDisk(projectId, id, version, language, result)
							.then(() => result)
					})
				}
			})
			.then(handleDownloadMediaFile)
			.then(result => {
				if (log) console.log(TAG, 'Finished downloading media item json for', id, result)
				// dispatch action so the manifest report on redux can be updated for this item
				const params = convertManifestItem(item, language, 'media', 'success_downloaded')
				store.dispatch(updateProcessingManifestStateItem(params))
				return result
			})
			.catch(error => {
				if (log) console.log(TAG, 'Error downloading media item json for', id, error)
				const params = convertManifestItem(item, language, 'media', 'failed')
				store.dispatch(updateProcessingManifestStateItem(params))
				// TODO sentry log
				return false
			})
	})
	return Promise.all(promises)
}

function handleDownloadMediaFile(mediaItem: GetMediaItemsShowResponse): Promise<boolean> {
	if (log) console.log(TAG, 'handleDownloadMediaFile', 'downloaded media item json', mediaItem.id, mediaItem)

	// if no file attached to media item we have nothing to download
	if (mediaItem.file === undefined) {
		return Promise.reject(new Error(`Media item ${mediaItem.id} does not contain a file`))
	}
	// image
	if (mediaItem.media_type === GetMediaItemsShowResponse.MediaTypeEnum.Image) {
		const id: number = mediaItem.file.id!
		const fileName: string = mediaItem.file.filename!
		if (log) console.log(TAG, 'handleDownloadMediaFile', 'start preDownloadImage for', id)
		return getConfiguration()
			.preDownloadImage(id, fileName, false)
			.then(result => {
				if (log) console.log(TAG, 'handleDownloadMediaFile', 'finish preDownloadImage for', id, result)
				return result
			})
	}
	// audio/video
	if (
		mediaItem.media_type === GetMediaItemsShowResponse.MediaTypeEnum.Audio ||
		mediaItem.media_type === GetMediaItemsShowResponse.MediaTypeEnum.Video
	) {
		const type: FileType =
			mediaItem.media_type === GetMediaItemsShowResponse.MediaTypeEnum.Audio ? FileType.AUDIO : FileType.VIDEO
		const id: number = mediaItem.file.id!
		const fileName: string = mediaItem.file.filename!
		const url: string = mediaItem.file.original_url!
		const filePath: string = getConfiguration().getFileDownloadPath(type, fileName)
		if (log) console.log(TAG, 'handleDownloadMediaFile', 'start checkFileExists', id)
		return getConfiguration()
			.checkFileExists(filePath)
			.then((exists: boolean) => {
				if (log) console.log(TAG, 'handleDownloadMediaFile', 'finish checkFileExists', id, exists)
				if (exists) {
					return true
				}
				if (log) console.log(TAG, 'handleDownloadMediaFile', 'start downloadFile for', id)
				return getConfiguration()
					.downloadFile(type, fileName, url)
					.then(result => {
						if (log) console.log(TAG, 'handleDownloadMediaFile', 'finish downloadFile for', id, result)
						return true
					})
			})
	}
	return Promise.reject(new Error(`Unsupported media type when downloading: ${mediaItem.media_type} ${mediaItem.id}`))
}

/**
 * Triggers the call to load the manifest so we can handle loading a screen or collection based on the user inputted id from the app-wide keypad entry screen.
 * @param action contains the id of collection or screen to load
 */
function* handleLoadItemOnKeypadEntry(action: LoadItemOnKeypadEntryParams): SagaIterator {
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId
	const { keypadNumber, itemType, itemId } = action.payload

	let availableItemsIds: number[] = []

	if (itemType === ScannerItemType.Collection) {
		const collectionItems = state.collectionlist.collectionItems.get(itemId)
		const items: CollectionItem[] = collectionItems && collectionItems.items ? collectionItems.items : []
		availableItemsIds = items.map(item => Number(item?.item_id))
	}

	// check if manifest exists on disk first
	const manifest: Manifest | undefined = yield call(getConfiguration().loadAppManifestFromDisk, projectId)
	if (manifest) {
		yield call(handleLoadKeypadEntryItemAfterFetchingManifest, action.payload, manifest, availableItemsIds)
	} else {
		const devicePlatform = getConfiguration().getPlatform()
		const deviceUdid = getConfiguration().getDeviceUUID()
		yield put(apiActions.fetchManifest.started({ devicePlatform, deviceUdid, keypadNumber, isForSync: false }))
	}
}

/**
 * Handles the loading of either a collection or screen details based on the user inputted id from the app-wide keypad entry screen.
 * @param action contains the id of collection or screen to load
 */
function* handleLoadKeypadEntryItemAfterFetchingManifest(
	payload: LoadItemOnKeypadEntry,
	manifest: Manifest,
	availableItemsIds?: number[],
): SagaIterator {
	const state = store.getState()
	const language = getCurrentLanguageCode(state)
	const { projectId } = state.settings.appInfo
	const platform = getConfiguration().getPlatform()
	const deviceUUID = getConfiguration().getDeviceUUID()

	const { keypadNumber } = payload

	// finds the keypad item based on a match from the user-set ids and the inputted id by the user
	const keypadItems: ManifestKeypadNumbers[] = manifest.keypad_numbers
		? manifest.keypad_numbers.filter(item => {
				if (availableItemsIds && availableItemsIds.length > 0 && item.item_number === keypadNumber) {
					return availableItemsIds.indexOf(item.item_id!) > -1
				}

				return item.item_number === keypadNumber
			})
		: []

	if (keypadItems.length === 0) {
		// unable to find the keypad item, so we dispatch a failed action
		yield put(loadItemOnKeypadEntry.failed({ params: { ...payload }, error: new Error() }))
		return
	}

	const items: KeypadItem[] = []
	const screenItemDict: Record<number, KeypadItem> = {}
	const collectionItemDict: Record<number, KeypadItem> = {}

	// keypadItems might contain the same item multiple times if it's both inside/outside of collection or if it belongs to multiple collections
	for (const keypadItem of keypadItems) {
		// grab the main item id to use as main reference
		const mainId: number = keypadItem.item_id!

		if (keypadItem.item_type === ManifestKeypadNumbers.ItemTypeEnum.Screen) {
			// keypad item is of type SCREEN
			const screenItems: ItemCompact[] = manifest.screens ? manifest.screens.filter(item => item.id === mainId) : []

			for (const screenItem of screenItems) {
				const storedItem: ScreenItem | undefined = yield call(
					getConfiguration().loadScreenFromDisk,
					projectId,
					mainId,
					screenItem.version!,
					language,
				)

				if (storedItem) {
					const item = screenItemDict[mainId] || {
						type: ManifestKeypadNumbers.ItemTypeEnum.Screen,
						subtype: storedItem.screen_type,
						id: mainId,
						version: screenItem.version!,
						language,
						title: storedItem.title,
						icon: storedItem.cover_image,
						collectionIds: keypadItem.collection_id ? [keypadItem.collection_id] : undefined,
					}

					// No collection_id means items should always be available
					if (!keypadItem.collection_id) {
						item.collectionIds = undefined
					} else if (
						item.collectionIds && // Only add new collection_id if this item already depends on another
						item.collectionIds.indexOf(keypadItem.collection_id) === -1
					) {
						item.collectionIds.push(keypadItem.collection_id)
					}

					if (!screenItemDict[mainId]) {
						items.push(item)
						screenItemDict[mainId] = item
					}
				} else {
					const params: apiActions.ScreenRequestParams = {
						devicePlatform: platform,
						deviceUdid: deviceUUID,
						screenId: mainId,
						screenVersion: screenItem.version!,
						accountId: screenItem.account_id,
						language,
						isPreCaching: false,
					}
					yield put(apiActions.fetchScreen.started(params))
					const item = screenItemDict[mainId] || {
						type: ManifestKeypadNumbers.ItemTypeEnum.Screen,
						id: mainId,
						version: screenItem.version!,
						language,
						collectionIds: keypadItem.collection_id ? [keypadItem.collection_id] : undefined,
					}

					// No collection_id means items should always be available
					if (!keypadItem.collection_id) {
						item.collectionIds = undefined
					} else if (
						item.collectionIds && // Only add new collection_id if this item already depends on another
						item.collectionIds.indexOf(keypadItem.collection_id) === -1
					) {
						item.collectionIds.push(keypadItem.collection_id)
					}

					if (!screenItemDict[mainId]) {
						items.push(item)
						screenItemDict[mainId] = item
					}
				}
			}
		} else if (keypadItem.item_type === ManifestKeypadNumbers.ItemTypeEnum.Collection) {
			const collectionItems: ItemCompact[] = manifest.collections
				? manifest.collections.filter(item => item.id === mainId)
				: []

			for (const collectionItem of collectionItems) {
				const storedItem: Collection | undefined = yield call(
					getConfiguration().loadCollectionDetailsFromDisk,
					projectId,
					mainId,
					collectionItem.version!,
					language,
				)

				if (storedItem) {
					const item = collectionItemDict[mainId] || {
						type: ManifestKeypadNumbers.ItemTypeEnum.Collection,
						subtype: storedItem.subtype,
						id: mainId,
						version: collectionItem.version!,
						language,
						title: storedItem.title,
						icon: storedItem.cover_image_grid || storedItem.cover_image,
						collectionIds: keypadItem.collection_id ? [keypadItem.collection_id] : undefined,
					}

					// No collection_id means items should always be available
					if (!keypadItem.collection_id) {
						item.collectionIds = undefined
					} else if (
						item.collectionIds && // Only add new collection_id if this item already depends on another
						item.collectionIds.indexOf(keypadItem.collection_id) === -1
					) {
						item.collectionIds.push(keypadItem.collection_id)
					}

					if (!collectionItemDict[mainId]) {
						items.push(item)
						collectionItemDict[mainId] = item
					}
				} else {
					const params: apiActions.CollectionRequestParams = {
						devicePlatform: platform,
						deviceUdid: deviceUUID,
						collectionId: mainId,
						collectionVersion: collectionItem.version!,
						accountId: collectionItem.account_id,
						language,
						isPreCaching: false,
					}
					yield put(apiActions.fetchCollection.started(params))
					const item = collectionItemDict[mainId] || {
						type: ManifestKeypadNumbers.ItemTypeEnum.Collection,
						id: mainId,
						version: collectionItem.version!,
						language,
						collectionIds: keypadItem.collection_id ? [keypadItem.collection_id] : undefined,
					}

					// No collection_id means items should always be available
					if (!keypadItem.collection_id) {
						item.collectionIds = undefined
					} else if (
						item.collectionIds && // Only add new collection_id if this item already depends on another
						item.collectionIds.indexOf(keypadItem.collection_id) === -1
					) {
						item.collectionIds.push(keypadItem.collection_id)
					}

					if (!collectionItemDict[mainId]) {
						items.push(item)
						collectionItemDict[mainId] = item
					}
				}
			}
		}
	}

	if (items.length === 0) {
		yield put(loadedKeypadItemsNotFound({ errorMessage: 'items not found' }))
		return
	}

	// Eager loading the collections linked to the keypad items
	// These collections might be used later to check whether the user has access to the keypad items
	const collectionsToLoad = new Set<number>()
	Object.values(screenItemDict).forEach(screenItem => {
		screenItem.collectionIds?.forEach(collectionId => collectionsToLoad.add(collectionId))
	})
	// These have already been loaded above
	Object.values(collectionItemDict).forEach(collectionItem => {
		collectionsToLoad.delete(collectionItem.id)
	})
	for (const collectionId of collectionsToLoad) {
		const { version, account_id } = manifest.collections?.find(item => item.id === collectionId) || {}
		if (version) {
			yield put(loadCollectionDetails({ id: collectionId, version, accountId: account_id, language }))
		}
	}

	yield put(loadedKeypadItemsComplete(items))
}

/**
 * Gets fired whenever a screen or collection has successfully been loaded via the disk.
 * Handles navigating the user to either a screen or collection based on a match to the stored keypad entry id in the store.
 * @param action contains the id and details of collection or screen to load
 */
// function* handleLoadedItemForKeypadEntryInDisk(action: LoadedScreenResultAction | CollectionDetailsResultAction): SagaIterator {
// 	const state = store.getState()
// 	const keypadEntryId: number | undefined = state.scanner.keypadEntryId

// 	// we have no keypadEntryId, this request is not for loading a keypad entry item
// 	if (keypadEntryId === undefined) return

// 	const isScreen: boolean = action.type.includes('SCREEN')
// 	const itemId: number = isScreen
// 		? (action as LoadedScreenResultAction).payload.id
// 		: (action as CollectionDetailsResultAction).payload.id

// 	// we found a match, do appropriate navigation. else, dispatch failed action
// 	if (keypadEntryId === itemId) {
// 		// log keypad entry action as done
// 		yield put(loadItemOnKeypadEntry.done({ params: { keypadNumber: itemId }, result: undefined }))

// 		if (isScreen) {
// 			const screen: ScreenItem = (action as LoadedScreenResultAction).payload.screen

// 			let itemSubtype
// 			switch(screen.screen_type) {
// 				case GetScreensShowResponse.ScreenTypeEnum.Story:
// 					itemSubtype = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Story
// 					break;
// 				case GetScreensShowResponse.ScreenTypeEnum.Web:
// 					itemSubtype = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web
// 					break;
// 				case GetScreensShowResponse.ScreenTypeEnum.Panorama:
// 					itemSubtype = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama
// 					break;
// 			}

// 			const params: CollectionItem = {
// 				item_id: screen.id,
// 				title: screen.title,
// 				version: screen.version,
// 				item_type: GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen,
// 				item_subtype: itemSubtype,
// 			}
// 			yield put(navigateToScreen(params))
// 		} else {
// 			const collection: Collection = (action as CollectionDetailsResultAction).payload.collection
// 			// important CollectionItem params: id, title, version and subtype
// 			const params: CollectionItem = {
// 				item_id: collection.id,
// 				title: collection.title,
// 				version: collection.version,
// 				item_type: GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection,
// 				item_subtype: collection.subtype === GetCollectionsShowResponse.SubtypeEnum.CollectionTour
// 					? GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour
// 					: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List,
// 			}
// 			yield put(navigateToCollection(params))
// 		}
// 	} else {
// 		yield put(loadItemOnKeypadEntry.failed({ params: { keypadNumber: itemId }, error: new Error() }))
// 	}
// }

/**
 * Gets fired whenever a screen or collection has successfully been loaded via the network.
 * Handles navigating the user to either a screen or collection based on a match to the stored keypad entry id in the store.
 * @param action contains the id and details of collection or screen to load
 */
// function* handleCompletedItemForKeypadEntry(action: apiActions.LoadScreenFromNetworkResult | apiActions.LoadCollectionFromNetworkResult): SagaIterator {
// 	const state = store.getState()
// 	const keypadEntryId: number | undefined = state.scanner.keypadEntryId

// 	// we have no keypadEntryId, this request is not for loading a keypad entry item
// 	if (keypadEntryId === undefined) return

// 	const isScreen: boolean = action.type.includes('SCREEN')
// 	const itemId: number = isScreen
// 		? (action as apiActions.LoadScreenFromNetworkResult).payload.params.screenId
// 		: (action as apiActions.LoadCollectionFromNetworkResult).payload.params.collectionId

// 	// we found a match, do appropriate navigation. else, dispatch failed action
// 	if (keypadEntryId === itemId) {
// 		// log keypad entry action as done
// 		yield put(loadItemOnKeypadEntry.done({ params: { keypadNumber: itemId }, result: undefined }))

// 		if (isScreen) {
// 			const screen: ScreenItem = (action as apiActions.LoadScreenFromNetworkResult).payload.result

// 			let itemSubtype
// 			switch(screen.screen_type) {
// 				case GetScreensShowResponse.ScreenTypeEnum.Story:
// 					itemSubtype = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Story
// 					break;
// 				case GetScreensShowResponse.ScreenTypeEnum.Web:
// 					itemSubtype = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web
// 					break;
// 				case GetScreensShowResponse.ScreenTypeEnum.Panorama:
// 					itemSubtype = GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama
// 					break;
// 			}

// 			// important CollectionItem params: id, title, version and subtype
// 			const params: CollectionItem = {
// 				item_id: screen.id,
// 				title: screen.title,
// 				version: screen.version,
// 				item_type: GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen,
// 				item_subtype: itemSubtype,
// 			}
// 			yield put(navigateToScreen(params))
// 		} else {
// 			const collection: Collection = (action as apiActions.LoadCollectionFromNetworkResult).payload.result
// 			// important CollectionItem params: id, title, version and subtype
// 			const params: CollectionItem = {
// 				item_id: collection.id,
// 				title: collection.title,
// 				version: collection.version,
// 				item_type: GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection,
// 				item_subtype: collection.subtype === GetCollectionsShowResponse.SubtypeEnum.CollectionTour
// 					? GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour
// 					: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List,
// 			}
// 			yield put(navigateToCollection(params))
// 		}
// 	} else {
// 		yield put(loadItemOnKeypadEntry.failed({ params: { keypadNumber: itemId }, error: new Error() }))
// 	}
// }

/**
 * Gets fired whenever a screen or collection has unsuccessfully been loaded via the network.
 * This updates the keypad entry status in the store so we can show an appropriate error message in the app-wide keypad entry screen.
 * @param action contains the id and details of collection or screen to load
 */
// function* handleFailedItemForKeypadEntry(action: apiActions.LoadScreenFromNetworkResultFailure | apiActions.LoadCollectionItemsFromNetworkResultFailure): SagaIterator {
// 	const state = store.getState()
// 	const keypadEntryId: number | undefined = state.scanner.keypadEntryId

// 	// we have no keypadEntryId, this request is not for loading a keypad entry item
// 	if (keypadEntryId === undefined) return

// 	const itemId: number = (action as apiActions.LoadScreenFromNetworkResultFailure).payload.params.screenId
// 		? (action as apiActions.LoadScreenFromNetworkResultFailure).payload.params.screenId
// 		: (action as apiActions.LoadCollectionItemsFromNetworkResultFailure).payload.params.collectionId

// 	// check if the collection/screen id matches the keypad entry id
// 	if (keypadEntryId === itemId) {
// 		yield put(loadItemOnKeypadEntry.failed({ params: { keypadNumber: itemId }, error: new Error() }))
// 	}
// }

// Saves downloaded manifest to disk so we can easily reuse it for the AppWideKeypadEntry feature
function* handleLoadedManifestFromNetwork(action: apiActions.LoadManifestFromNetworkResult): SagaIterator {
	const manifest: Manifest | undefined = action.payload.result
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId

	try {
		yield call(getConfiguration().saveAppManifestToDisk, projectId, manifest)
	} catch (error) {
		// Error saving data
	}
}

export default function* (): SagaIterator {
	// initial load trigger for app meta data
	yield takeEvery(loadAppMetaData, handleLoadAppMetaData)

	// when meta data is loaded from network, store to disk
	yield takeEvery(apiActions.fetchAppMetaData.done, handleLoadedMetaDataFromNetwork)

	// handle initial unpacking of assets
	yield takeEvery(unpackInitialAssets, handleUnpackInitialAssets)

	// handle checking for meta data updates
	yield takeEvery(checkForUpdates, handleCheckForUpdates)

	// handle setting selected app language on strings localizer
	yield takeEvery(appLanguageSelected, handleLanguageSelected)

	// handle downloading the items from the manifest
	// yield takeEvery(apiActions.fetchManifest.done, handleDownloadedManifest) // disable for now, because it's loading all data from manifest
	yield takeEvery(apiActions.fetchManifest.done, handleLoadedManifestFromNetwork)

	// initial trigger to load the manifest so we can find the item matching the user-inputted id
	yield takeEvery(loadItemOnKeypadEntry.started, handleLoadItemOnKeypadEntry)

	// navigates the user to the appropriate page once item is loaded via disk
	// yield takeEvery([loadedScreenFromDisk, loadedCollectionDetailsFromDisk], handleLoadedItemForKeypadEntryInDisk)

	// navigates the user to the appropriate page once item is loaded via network
	// yield takeEvery([apiActions.fetchCollection.done, apiActions.fetchScreen.done], handleCompletedItemForKeypadEntry)

	// item has unsuccessfully been loaded via the network
	// yield takeEvery([apiActions.fetchCollection.failed, apiActions.fetchScreen.failed], handleFailedItemForKeypadEntry)
}
